/**
 * Notification model for the app-wide toast notification stack.
 *
 * Mirrors the legacy `notification.ts` model: a `Notification` is a fully
 * formed stack entry (with a unique `id` assigned by the provider), while a
 * `NotificationPayload` is what consumers pass to `pushNotif` (no `id`,
 * optional `type` and `canExpire`).
 */

/**
 * Notification type. 'success' renders the success toast variant; anything
 * else renders the alert variant (blueberry `Toast` supports only
 * 'success' | 'alert').
 */
export const NotificationType = {
    Success: 'success',
    Danger: 'danger',
};

/**
 * @typedef {Object} NotificationAction
 * @property {string} text - Label of the action button.
 * @property {boolean} [isLoading] - Whether the action button is in its loading state.
 * @property {(notification: Notification) => void} onClick - Called with the notification when the action is clicked.
 */

/**
 * @typedef {Object} Notification
 * @property {number} id - Unique id, assigned by the provider via a running counter.
 * @property {('success'|'danger')} type - Visual variant of the toast.
 * @property {React.ReactNode} message - Title of the toast.
 * @property {React.ReactNode} [description] - Optional description rendered under the title.
 * @property {() => void} [onClose] - Called when the toast is closed.
 * @property {NotificationAction} [action] - Optional action button.
 * @property {boolean} [canExpire] - Auto-expire after 5000ms when true (default true).
 */

/**
 * @typedef {Object} NotificationPayload
 * @property {('success'|'danger')} [type] - Defaults to 'success'.
 * @property {boolean} [canExpire] - Defaults to true.
 * @property {React.ReactNode} message - Title of the toast.
 * @property {React.ReactNode} [description] - Optional description rendered under the title.
 * @property {() => void} [onClose] - Called when the toast is closed.
 * @property {NotificationAction} [action] - Optional action button.
 */

/** Default auto-expire delay, matching the legacy system. */
export const AUTO_EXPIRE_MS = 5000;
